import { Checkbox, TableCell, TableHead, TableRow, TableSortLabel } from '@mui/material';
import Box from '@mui/material/Box';
import { visuallyHidden } from '@mui/utils';

/**
 * Modern design system structural blueprint for custom table header column cells.
 */
export interface EnhancedHeadCell {
  /** Unique look-up property key targeting a data model column field */
  id: string;
  /** Primary text string rendered inside the column header framework */
  label: string;
  /** If true, formats and aligns numerical contents to the right hand margins */
  numeric?: boolean;
  /** If true, completely strips layout padding constraints off the inner cell canvas */
  disablePadding?: boolean;
  /** Optional override switch toggling click-to-sort capabilities on individual targets */
  sortable?: boolean;
  /** Precision layout typography alignment controller mapping standard MUI strategies */
  align?: 'left' | 'center' | 'right' | 'justify' | 'inherit';
}

/**
 * Property contract for orchestrating interactive configurations on the EnhancedTableHead component.
 */
interface EnhancedTableHeadProps {
  /** Static analytical schema configurations specifying individual rendering cell components */
  headCells: readonly EnhancedHeadCell[];
  /** Integer index mapping out the runtime global accumulation of checked entries */
  numSelected?: number;
  /** Total pagination volume size for evaluated list entries across current views */
  rowCount?: number;
  /** Interactive operational toggle controlling mass item checklist checkboxes on the left */
  showCheckbox?: boolean;
  /** Mutation event callback capturing checklist clicks to toggle every item state */
  onSelectAllClick?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  /** Reactive layout flag targeting active alphanumeric string layout directions */
  sortDirection?: 'asc' | 'desc';
  /** Field column identifier lookup string marking active metadata sorting columns */
  orderByField?: string;
  /** Global query engine orchestration listener alerting parents to apply new sort parameters */
  onRequestSort?: (event: React.MouseEvent<unknown>, property: string) => void;
  /** Exclusions blacklist catalog recording field identification keys forced out of view layouts */
  hiddenColumnIds?: string[];
  /** Global visibility toggle flag forcing the entire header node layout to clear out of the DOM */
  hideHeader?: boolean;
}

/**
 * Functional presentation component rendering interactive header bars for standard MUI tables.
 * Dynamically encapsulates client-driven list ordering logic, global checklist orchestration,
 * accessibility label injections, and runtime user authorization column hiding.
 *
 * @param props - Core configuration metrics following the EnhancedTableHeadProps schema contract.
 * @returns Strongly typed interactive layout framework for structural table headers.
 */
export const EnhancedTableHead = ({
  headCells,
  numSelected = 0,
  rowCount = 0,
  showCheckbox = false,
  onSelectAllClick,
  sortDirection,
  orderByField,
  onRequestSort,
  hiddenColumnIds = [],
  hideHeader = false
}: EnhancedTableHeadProps) => {
  // Early return execution preventing rendering layouts when suppressed by configuration
  if (hideHeader) return null;

  // Filters out hidden column entries prior to processing layout maps (Clean Code best practices)
  const visibleHeadCells = headCells.filter((cell) => !hiddenColumnIds.includes(cell.id));

  const createSortHandler = (property: string) => (event: React.MouseEvent<unknown>) => {
    if (onRequestSort) {
      onRequestSort(event, property);
    }
  };
  return (
    <TableHead sx={{ bgcolor: '#ffffff !important' }}>
      <TableRow>
        {/* Dynamic conditional render injection supplying mass checklist indicators when authorized */}
        {showCheckbox && onSelectAllClick && (
          <TableCell padding="checkbox" sx={{ width: 50 }}>
            <Checkbox
              color="primary"
              indeterminate={numSelected > 0 && numSelected < rowCount}
              checked={rowCount > 0 && numSelected === rowCount}
              onChange={onSelectAllClick}
              inputProps={{ 'aria-label': 'select all items' }}
            />
          </TableCell>
        )}

        {/* Linear mapping stream plotting every visible header configuration into cells */}
        {visibleHeadCells.map((headCell) => {
          const isSortedField = orderByField === headCell.id;
          const isSortable = headCell.sortable !== false && !!onRequestSort;

          // Context-aware alignments evaluated fallback checks matching modern styling paradigms
          const cellAlignment = headCell.align || (headCell.numeric ? 'right' : 'left');

          return (
            <TableCell
              key={headCell.id}
              align={cellAlignment}
              padding={headCell.disablePadding ? 'none' : 'normal'}
              sortDirection={isSortedField ? sortDirection : false}
              sx={{ fontWeight: 700, color: '#475569', borderBottom: '2px solid #e2e8f0' }}>
              {isSortable ? (
                <TableSortLabel
                  active={isSortedField}
                  direction={isSortedField ? sortDirection : 'asc'}
                  onClick={createSortHandler(headCell.id)}>
                  {headCell.label}
                  {isSortedField ? (
                    <Box component="span" sx={visuallyHidden}>
                      {sortDirection === 'desc' ? 'sorted descending' : 'sorted ascending'}
                    </Box>
                  ) : null}
                </TableSortLabel>
              ) : (
                headCell.label
              )}
            </TableCell>
          );
        })}
      </TableRow>
    </TableHead>
  );
};

export default EnhancedTableHead;
